import {
  insertNodeAt,
  moveArrayElement,
  removeNode,
  useSortable,
} from '@vueuse/integrations/useSortable';
import type {SortableEvent} from 'sortablejs';
import {computed, isRef, MaybeRef, nextTick, reactive, Ref} from 'vue';
import {ComposerTranslation, useI18n} from 'vue-i18n';

export type DragonDropOptions<T> = {
  handle?: string;
  group?: string;
  onRemove?: (item: T, index: number) => void;
  t?: ComposerTranslation<{}, 'en' | 'fr'>;
};

/**
 * `useDragAndDrop`: Accessible Drag and Drop Lists
 *
 * __Note:__ Making accessible DND is a hard thing made harder by weird browser inconsistencies
 * and funky interactions between Vue and Sortable.js. We've tried to add comments explaining
 * what lead to some of the non-obvious decisions in this code.
 *
 * @param el
 * @param list
 * @param options
 */
export function useDragAndDrop<T>(
  el: Ref<HTMLElement | null | undefined>,
  list: MaybeRef<T[]>,
  options: DragonDropOptions<T> = {}
) {
  const handle = options.handle || 'handle';

  /**
   * Provides KeyboardControls for ordering items in a list.
   */
  const keyboardControls = reactive({
    index: null as number | null,

    /**
     * Toggle keyboard reorder mode for the item at the given index.
     *
     * @param index
     */
    toggle(index: number) {
      if (this.index === index) {
        this.disable();
      } else {
        this.enable(index);
      }
    },

    /**
     * Enable keyboard reorder mode for the item at the given index.
     *
     * @param index
     */
    enable(index: number) {
      this.index = index;
    },

    /**
     * Disable keyboard reorder mode.
     */
    disable() {
      this.index = null;
    },

    /**
     * Move the currently focused item up/left in the list.
     */
    async moveUp() {
      if (this.index === null || this.index === 0) return;

      const currentIndex = this.index;
      const newIndex = currentIndex - 1;
      moveArrayElement(list, currentIndex, newIndex);

      this.index = newIndex;

      await this.refocusHandle(newIndex);
    },

    /**
     * Move the currently focused item down/right in the list.
     */
    async moveDown() {
      const items = isRef(list) ? list.value : list;
      if (this.index === null || this.index === items.length - 1) return;

      const currentIndex = this.index!;
      const newIndex = currentIndex + 1;
      moveArrayElement(list, currentIndex, newIndex);

      this.index = newIndex;

      await this.refocusHandle(newIndex);
    },

    /**
     * Focus on the handle of the currently focused item.
     */
    async refocusHandle(index: number) {
      /**
       * Note: For a reason I cannot explain, we needed to wait two ticks
       * for the AssignmentTask table to work with keyboard controls.
       */
      await nextTick();
      await nextTick();

      if (index === null) return;

      const handles = el.value?.querySelectorAll('button.' + handle) ?? [];

      if (handles[index]) {
        (handles[index] as HTMLElement).focus();

        /**
         * Note: Most of the time this is not necessary, but we've noticed that for _some_ use
         * cases in _some_ browsers, focus is sometimes lost when moving items. This causes
         * the `blur` event to be triggered, which disables the keyboard controls. In this case,
         * we re-enable the keyboard controls for the item at the given index. When that doesn't
         * happen, the following line is a no-op.
         */
        this.enable(index); // Just incase
      }
    },
  });

  /**
   * # useSortable
   *
   * ## Notes on Transferring Between Lists
   *
   * Sortable.js allows you to transfer items between lists by using the `group` option.
   *
   * The order of operations for this process is:
   * 1. the `onAdd` method is called on the target list
   * 2. the `onRemove` method is called on the source list.
   *
   * For siblings lists this works well.
   *
   * For nested-lists this can cause issues.
   *
   * The parent-list must _always_ be modified _after_ the child-list. This is because modifying
   * the parent list first can change the index of the child list itself. As a result, the child's
   * callback _may_ end up modifying the wrong item in the parent list.
   *
   * To get around this, we use the `nextTick` function to ensure that the child list is modified
   * before the parent.
   *
   * Moving an item from Parent->Child:
   * - After 1-tick add the item to the child list
   * - After 2-ticks remove the item from the parent list
   *
   * Moving an item from Child->Parent:
   * - After 1-tick remove the item from the child list
   * - After 2-ticks add the item to the parent list
   */
  useSortable(el, list, {
    handle: '.' + handle,
    fallbackOnBody: true,
    animation: 100,
    group: options.group,

    /**
     * Note: the `useSortable` source updates the DOM _before_ updating the model
     *
     * @see https://github.com/vueuse/vueuse/blob/main/packages/integrations/useSortable/index.ts
     */
    onAdd(event: SortableEvent) {
      let item: T | null = null;

      if (event.item.hasAttribute('data-json')) {
        item = JSON.parse(event.item.getAttribute('data-json') || '{}') as T;
      } else {
        item = event.item.getAttribute('data-value') as unknown as T;
      }

      if (item) {
        const movingFromParentToChild = event.from.contains(event.to);

        if (movingFromParentToChild) {
          addToList(event.newIndex!, item);
        } else {
          nextTick(() => addToList(event.newIndex!, item));
        }

        insertNodeAt(event.to, event.item, event.newIndex!);
      }
    },
    /**
     * Note: the `useSortable` source updates the DOM _before_ updating the model
     *
     * @see https://github.com/vueuse/vueuse/blob/main/packages/integrations/useSortable/index.ts
     */
    onRemove(event: SortableEvent) {
      const movingFromParentToChild = event.from.contains(event.to);

      if (movingFromParentToChild) {
        nextTick(() => removeFromList(event.oldIndex!));
      } else {
        removeFromList(event.oldIndex!);
      }

      removeNode(event.item);
    },
  });

  /**
   * Note:
   * - The `useSortable` source code makes changes to the model in a nextTick callback.
   *
   * @see https://github.com/vueuse/vueuse/blob/main/packages/integrations/useSortable/index.ts
   */
  function addToList(index: number, item: any) {
    nextTick(() => {
      if (isRef(list)) {
        const newList = [...list.value];
        newList.splice(index, 0, item);
        list.value = newList;
      } else {
        list.splice(index, 0, item);
      }
    });
  }

  /**
   * Note:
   * - The `useSortable` source code makes changes to the model in a nextTick callback.
   *
   * @see https://github.com/vueuse/vueuse/blob/main/packages/integrations/useSortable/index.ts
   */
  function removeFromList(index: number) {
    nextTick(() => {
      let removedItem: T;
      if (isRef(list)) {
        const items = [...list.value];
        [removedItem] = items.splice(index, 1);
        list.value = items;
      } else {
        [removedItem] = list.splice(index, 1);
      }

      if (options.onRemove) {
        options.onRemove(removedItem, index);
      }
    });
  }

  const t = options.t || useI18n().t;

  const isDragging = computed(() => keyboardControls.index);

  return {
    isDragging,
    props(index: number) {
      const items = isRef(list) ? list.value : list;
      const isStringOrNumber = ['string', 'number'].includes(typeof items[index]);

      const label = isStringOrNumber ? '(' + items[index] + ')' : '';

      return {
        class: handle,
        tabindex: '0',
        'aria-label':
          keyboardControls.index === index
            ? t('actions.exitReorderMode', {position: index + 1, label})
            : t('actions.reorderItem', {position: index + 1, label}),
      };
    },
    listeners(index: number) {
      return {
        blur() {
          if (keyboardControls.index === index) {
            keyboardControls.disable();
          }
        },
        keydown(e: KeyboardEvent) {
          switch (e.key) {
            case 'ArrowUp':
              e.stopPropagation();
              e.preventDefault();
              keyboardControls.moveUp();
              break;
            case 'ArrowDown':
              e.stopPropagation();
              e.preventDefault();
              keyboardControls.moveDown();
              break;
            case 'Enter':
              e.stopPropagation();
              e.preventDefault();
              keyboardControls.toggle(index);
              break;
            case 'Escape':
              e.stopPropagation();
              e.preventDefault();
              keyboardControls.disable();
              break;
          }
        },
      };
    },
    remove: removeFromList,
  };
}
